import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, HelpCircle, Zap, MessageCircle, Phone, Mail } from 'lucide-react';

const FAQSection = () => {
    const [openIndex, setOpenIndex] = useState(null);
    const [isVisible, setIsVisible] = useState(false);
    const sectionRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.1 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => observer.disconnect();
    }, []);

    const faqs = [
        {
            question: 'How do I track my shipment?',
            answer: 'Use your tracking number in the Track Shipment feature on the homepage or dashboard. You\'ll get real-time status updates, current location, and estimated delivery date.',
            category: 'Tracking'
        },
        {
            question: 'Why has my delivery been delayed?',
            answer: 'Occasional delays may occur due to traffic, weather, or customs inspections. Check the detailed tracking timeline first. For further clarification, call support at +91-84477-38498 or email connect@roadexpressfreight.com.',
            category: 'Delivery'
        },
        {
            question: 'Can I update the delivery address after booking?',
            answer: 'Yes—as long as the shipment hasn\'t been dispatched. Before dispatch, go to My Shipments, select your booking, and update the address. Once dispatched, reach out to support immediately with your tracking number—the sooner, the better.',
            category: 'Booking'
        },
        {
            question: 'How do I use the mobile app?',
            answer: (
                <div className="space-y-3">
                    <p className="text-gray-600">Follow these simple steps to get started:</p>
                    <ul className="space-y-2">
                        <li className="flex items-start">
                            <div className="w-2 h-2 bg-[#F05A28] rounded-full mt-2 mr-3 flex-shrink-0"></div>
                            <span>Download the RoadExpress Freight app via Google Play Store</span>
                        </li>
                        <li className="flex items-start">
                            <div className="w-2 h-2 bg-[#F05A28] rounded-full mt-2 mr-3 flex-shrink-0"></div>
                            <span>Use your RoadExpress credentials to log in securely</span>
                        </li>
                        <li className="flex items-start">
                            <div className="w-2 h-2 bg-[#F05A28] rounded-full mt-2 mr-3 flex-shrink-0"></div>
                            <span>Access features including route-specific truck availability, real-time trip tracking, e-POD uploads, and fare negotiations</span>
                        </li>
                    </ul>
                </div>
            ),
            category: 'Mobile App'
        },
        {
            question: 'What do I do if my e-POD fails to upload?',
            answer: 'Check your internet connectivity, then retry within the app. If it still fails, contact our technical support at connect@roadexpressfreight.com with your trip ID and screenshots (if possible).',
            category: 'Technical'
        },
        {
            question: 'How are shipping rates calculated?',
            answer: 'Our rates are based on distance, vehicle type, cargo weight, delivery timeline, and route availability. Use our instant quote calculator for accurate pricing, or contact our sales team for volume discounts.',
            category: 'Pricing'
        },
        {
            question: 'What payment methods do you accept?',
            answer: 'We accept UPI, net banking, credit/debit cards, and digital wallets. For enterprise customers, we also offer invoice-based payments with flexible credit terms.',
            category: 'Payment'
        },
        {
            question: 'Do you provide insurance for shipments?',
            answer: 'Yes, we offer comprehensive cargo insurance options. Basic coverage is included with all shipments, and you can opt for enhanced coverage for high-value goods during the booking process.',
            category: 'Insurance'
        }
    ];

    const toggleFAQ = (index) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    const categories = [...new Set(faqs.map(faq => faq.category))];

    return (
        <section
            ref={sectionRef}
            className="w-full bg-gray-100 relative overflow-hidden"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">

                {/* Header Section */}
                <div className="mb-16">
                    <div
                        className={`transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                            }`}
                    >
                        <div className="flex items-center mb-6">
                            <div className="w-12 h-px bg-[#F05A28] mr-4"></div>
                            <span className="text-[#F05A28] font-semibold tracking-wider text-sm uppercase flex items-center">
                                <Zap className="w-4 h-4 mr-2" />
                                Support Center
                            </span>
                        </div>

                        <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                            Answers you need,
                            <br />
                            <span className="text-[#F05A28] italic">when you need them</span>
                        </h2>

                        <p className="text-xl text-gray-600 leading-relaxed max-w-3xl">
                            Get instant answers to common questions about our logistics services, tracking,
                            mobile app, and support. Can't find what you're looking for? Our team is here to help.
                        </p>
                    </div>
                </div>

                <div className="grid lg:grid-cols-12 gap-12">

                    {/* FAQ Content */}
                    <div className="lg:col-span-8">
                        <div
                            className={`transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                                }`}
                            style={{ transitionDelay: '0.2s' }}
                        >
                            <div className="space-y-4">
                                {faqs.map((faq, index) => {
                                    const isOpen = openIndex === index;

                                    return (
                                        <div
                                            key={index}
                                            className={`bg-white rounded-2xl border border-gray-100 transition-all duration-500 hover:shadow-lg ${isOpen ? 'shadow-xl' : 'shadow-sm'
                                                }`}
                                            style={{
                                                transform: `translateY(${isVisible ? '0' : '20px'})`,
                                                opacity: isVisible ? 1 : 0,
                                                transitionDelay: `${0.4 + index * 0.1}s`
                                            }}
                                        >
                                            <button
                                                onClick={() => toggleFAQ(index)}
                                                className="w-full flex justify-between items-start px-8 py-6 text-left group"
                                            >
                                                <div className="flex-1 pr-4">
                                                    <div className="flex items-center mb-2">
                                                        <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-orange-50 text-[#F05A28] border border-orange-200 mr-3">
                                                            {faq.category}
                                                        </span>
                                                    </div>
                                                    <h3 className="text-xl font-bold text-gray-900 group-hover:text-gray-800 transition-colors duration-300">
                                                        {faq.question}
                                                    </h3>
                                                </div>

                                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300 ${isOpen
                                                        ? 'bg-[#F05A28] text-white transform rotate-180'
                                                        : 'bg-gray-100 text-gray-600 group-hover:bg-gray-200'
                                                    }`}>
                                                    <ChevronDown className="w-5 h-5" />
                                                </div>
                                            </button>

                                            <div
                                                className={`overflow-hidden transition-all duration-500 ease-in-out ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                                                    }`}
                                            >
                                                <div className="px-8 pb-8">
                                                    <div className="border-t border-gray-100 pt-6">
                                                        <div className="text-gray-600 leading-relaxed text-lg">
                                                            {faq.answer}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Accent line */}
                                            <div className={`h-1 bg-[#F05A28] rounded-b-2xl transition-all duration-500 origin-left ${isOpen ? 'scale-x-100' : 'scale-x-0'
                                                }`}></div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Support Sidebar */}
                    <div className="lg:col-span-4">
                        <div
                            className={`transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                                }`}
                            style={{ transitionDelay: '0.6s' }}
                        >
                            <div className="bg-white rounded-3xl p-8 shadow-sm border border-gray-100 sticky top-8">
                                <div className="mb-8">
                                    <div className="w-16 h-16 bg-orange-50 rounded-2xl flex items-center justify-center mb-6">
                                        <MessageCircle className="w-8 h-8 text-[#F05A28]" />
                                    </div>
                                    <h3 className="text-2xl font-bold text-gray-900 mb-4">
                                        Need More Help?
                                    </h3>
                                    <p className="text-gray-600 leading-relaxed">
                                        Can't find the answer you're looking for? Our support team is available 24/7 to assist you.
                                    </p>
                                </div>

                                <div className="space-y-4">
                                    <a
                                        href="tel:+918447738498"
                                        className="flex items-center p-4 rounded-xl border-2 border-gray-200 hover:border-[#F05A28] hover:bg-orange-50 transition-all duration-300 group"
                                    >
                                        <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mr-4 group-hover:bg-green-200 transition-colors">
                                            <Phone className="w-6 h-6 text-green-600" />
                                        </div>
                                        <div>
                                            <div className="font-semibold text-gray-900">Call Support</div>
                                            <div className="text-sm text-gray-600">+91-844-773-8498</div>
                                        </div>
                                    </a>

                                    <a
                                        href="mailto:connect@roadexpressfreight.com"
                                        className="flex items-center p-4 rounded-xl border-2 border-gray-200 hover:border-[#F05A28] hover:bg-orange-50 transition-all duration-300 group"
                                    >
                                        <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mr-4 group-hover:bg-blue-200 transition-colors">
                                            <Mail className="w-6 h-6 text-blue-600" />
                                        </div>
                                        <div>
                                            <div className="font-semibold text-gray-900">Email Support</div>
                                            <div className="text-sm text-gray-600">connect@roadexpressfreight.com</div>
                                        </div>
                                    </a>
                                </div>

                                <div className="mt-8 p-4 bg-gradient-to-r from-orange-50 to-orange-100 rounded-xl border border-orange-200">
                                    <div className="flex items-start">
                                        <HelpCircle className="w-5 h-5 text-[#F05A28] mt-1 mr-3 flex-shrink-0" />
                                        <div>
                                            <div className="font-semibold text-gray-900 text-sm mb-1">Pro Tip</div>
                                            <div className="text-sm text-gray-600">
                                                Have your tracking number or trip ID ready when contacting support for faster assistance.
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default FAQSection;